import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { LOGO } from "@/lib/assets";
import { useLang } from "@/context/LanguageContext";
import { useAccount } from "@/context/AccountAuthContext";
import { Menu, X, User } from "lucide-react";

export default function Header() {
  const { t, lang, toggle } = useLang();
  const { user } = useAccount();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome;

  const sectionLinks = [{ id: "histoire", label: t.nav.story }];
  const routeLinks = [
    { to: "/kids-camp", label: t.nav.summer, testid: "nav-kidscamp" },
    { to: "/ateliers", label: t.nav.workshops, testid: "nav-workshops" },
    { to: "/evenements", label: t.nav.events, testid: "nav-events" },
    { to: "/reservation", label: t.nav.book, testid: "nav-reserver" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        solid ? "bg-[#6f7d54]/95 backdrop-blur-xl shadow-[0_8px_30px_rgba(0,0,0,0.25)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center" onClick={() => setOpen(false)}>
          <img src={LOGO} alt="Island Camps" className="h-11 md:h-12 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {sectionLinks.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => goTo(l.id)}
              className="font-body text-[15px] font-semibold text-[#f7efe0]/85 hover:text-white transition-colors tracking-wide"
            >
              {l.label}
            </button>
          ))}
          {routeLinks.map((l) => (
            <Link key={l.to} data-testid={l.testid} to={l.to} className="font-body text-[15px] font-semibold text-[#f7efe0]/85 hover:text-white transition-colors">
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="flex items-center rounded-full border border-[#f7efe0]/30 overflow-hidden text-[13px] font-bold"
          >
            <span className={`px-2.5 py-1 ${lang === "fr" ? "bg-[#e0b36a] text-[#6f7d54]" : "text-[#f7efe0]/70"}`}>FR</span>
            <span className={`px-2.5 py-1 ${lang === "en" ? "bg-[#e0b36a] text-[#6f7d54]" : "text-[#f7efe0]/70"}`}>EN</span>
          </button>

          <Link
            to="/mon-compte"
            data-testid="header-account-btn"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-[#f7efe0]/30 text-[#f7efe0] font-bold text-[14px] px-4 py-2.5 hover:bg-[#f7efe0]/10 transition-colors"
          >
            <User size={16} /> {user ? (user.name ? user.name.split(" ")[0] : "Mon compte") : "Connexion"}
          </Link>

          <Link
            to="/adhesion"
            data-testid="header-member-btn"
            className="hidden sm:inline-flex items-center rounded-full bg-[#e07a5f] hover:bg-[#cf6a50] text-white font-bold text-[14px] px-5 py-2.5 transition-colors shadow-sm"
          >
            {t.nav.member}
          </Link>

          <button
            className="lg:hidden text-[#f7efe0]"
            data-testid="mobile-menu-btn"
            onClick={() => setOpen((o) => !o)}
            aria-label="menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#6f7d54] border-t border-white/10 px-5 py-4 flex flex-col gap-2">
          {sectionLinks.map((l) => (
            <button key={l.id} data-testid={`mobile-nav-${l.id}`} onClick={() => goTo(l.id)} className="text-left font-body text-[#f7efe0]/90 py-2 text-lg">
              {l.label}
            </button>
          ))}
          {routeLinks.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="font-body text-[#f7efe0]/90 py-2 text-lg">{l.label}</Link>
          ))}
          <Link to="/carnet" onClick={() => setOpen(false)} className="font-body text-[#f7efe0]/90 py-2 text-lg">{t.nav.carnet}</Link>
          <Link to="/mon-compte" onClick={() => setOpen(false)} data-testid="mobile-account-btn" className="font-body text-[#f7efe0]/90 py-2 text-lg">{user ? "Mon compte" : "Connexion"}</Link>
          <Link to="/adhesion" onClick={() => setOpen(false)} data-testid="mobile-member-btn" className="mt-1 inline-flex justify-center items-center rounded-full bg-[#e07a5f] text-white font-bold px-5 py-3">
            {t.nav.member}
          </Link>
        </div>
      )}
    </header>
  );
}
